import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { NotificationService } from '@shared/services/notification.service';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { OptionalFieldComponent } from '@shared/components/optional-field/optional-field.component';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { Person } from '../../models/person.model';
import {
  CreatePersonEventRequest,
  PersonEvent,
  PersonLookupOption,
  ReligiousEventType,
  ReligiousEventTypeOption,
  religiousEventTypeOptions,
} from '../../models/person-event.model';
import { PersonEventService } from '../../services/person-event.service';
import { PersonService } from '../../services/person.service';
import {
  getPersonEventCategoryForKind,
  getPersonEventEditorErrorMessage,
} from '../../pages/person-event-editor-page/person-event-editor.utils';

type RelatedPersonMode = 'none' | 'system' | 'manual';
type ReligiousEventPlaceMode = 'unit' | 'location';

@Component({
  selector: 'app-person-religious-event-editor-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    DatePickerComponent,
    OptionalFieldComponent,
    UnitSelectorComponent,
  ],
  templateUrl: './person-religious-event-editor-form.component.html',
  styleUrl: './person-religious-event-editor-form.component.scss',
})
export class PersonReligiousEventEditorFormComponent implements OnInit, OnDestroy {
  @Input({ required: true }) person!: Person;
  @Input() religiousEvents: PersonEvent[] = [];
  @Input() currentEvent: PersonEvent | null = null;

  @Output() cancelled = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  readonly sacramentoOptions = religiousEventTypeOptions.filter(o => o.category === 'Sacrament');
  readonly bendicionOptions  = religiousEventTypeOptions.filter(o => o.category === 'Blessing');
  readonly anniversaryOptions = [
    { value: 25, label: 'Bodas de plata (25 años)' },
    { value: 40, label: 'Bodas de rubi (40 años)' },
    { value: 50, label: 'Bodas de oro (50 años)' },
    { value: 60, label: 'Bodas de diamante (60 años)' },
    { value: 65, label: 'Bodas de hierro (65 años)' },
    { value: 70, label: 'Bodas de gracia (70 años)' },
    { value: 75, label: 'Bodas de brillante (75 años)' },
  ];

  readonly form;
  readonly relatedPersonSearchControl;
  readonly religiousEventSearchControl;
  readonly filteredPeople$: Observable<PersonLookupOption[]>;
  filteredReligiousEventGroups$!: Observable<Array<{ label: string; options: ReligiousEventTypeOption[] }>>;

  isSaving = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly personEventService: PersonEventService,
    private readonly personService: PersonService,
    private readonly notifications: NotificationService,
  ) {
    this.form = this.fb.group({
      eventDate: [null as any, Validators.required],
      religiousEventType: [null as ReligiousEventType | null, Validators.required],
      placeMode: ['unit' as ReligiousEventPlaceMode],
      locationOrganizationUnitId: [null as string | null],
      anniversaryYears: [null as number | null],
      relatedPersonMode: ['none' as RelatedPersonMode],
      relatedPersonId: [null as string | null],
      relatedPersonName: [''],
      location: ['', Validators.maxLength(200)],
      notes: [''],
    });

    this.relatedPersonSearchControl = this.fb.control('');
    this.religiousEventSearchControl = this.fb.control<string | null>(null);

    this.filteredReligiousEventGroups$ = this.religiousEventSearchControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const term = typeof value === 'string' ? value.toLowerCase() : '';
        return [
          { label: 'Sacramentos', options: this.sacramentoOptions.filter(o => !term || o.label.toLowerCase().includes(term)) },
          { label: 'Bendiciones', options: this.bendicionOptions.filter(o => !term || o.label.toLowerCase().includes(term)) },
        ].filter(g => g.options.length > 0);
      }),
    );

    this.filteredPeople$ = this.relatedPersonSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(250),
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value !== 'string') {
          return of([]);
        }

        const term = value.trim();
        if (term.length < 2 || !this.supportsRelatedPerson) {
          return of([]);
        }

        return this.personService.searchPeople(term);
      }),
    );
  }

  ngOnInit(): void {
    this.religiousEventSearchControl.setValidators(this._religiousEventTypeValidator());
    this.religiousEventSearchControl.updateValueAndValidity({ emitEvent: false });

    this.form.get('religiousEventType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncAnniversaryRules();
        this.syncRelatedPersonRules();
        this.religiousEventSearchControl.updateValueAndValidity({ emitEvent: false });
      });

    this.form.get('relatedPersonMode')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncRelatedPersonRules());

    this.form.get('placeMode')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncPlaceRules());

    if (this.currentEvent) {
      const placeMode: ReligiousEventPlaceMode = this.currentEvent.locationOrganizationUnitId ? 'unit' : 'location';
      const relatedPersonMode: RelatedPersonMode =
        this.currentEvent.relatedPersonId ? 'system' :
        this.currentEvent.relatedPersonName ? 'manual' :
        'none';

      const editedReligiousOpt = religiousEventTypeOptions.find(o => o.value === this.currentEvent?.religiousEventType);
      if (editedReligiousOpt) {
        this.religiousEventSearchControl.setValue(editedReligiousOpt.label, { emitEvent: false });
      }

      this.form.patchValue({
        eventDate: this.fromIsoDate(this.currentEvent.eventDate),
        religiousEventType: this.currentEvent.religiousEventType ?? null,
        placeMode,
        locationOrganizationUnitId: this.currentEvent.locationOrganizationUnitId ?? null,
        anniversaryYears: this.currentEvent.anniversaryYears ?? null,
        relatedPersonMode,
        relatedPersonId: this.currentEvent.relatedPersonId ?? null,
        relatedPersonName: this.currentEvent.relatedPersonId ? '' : (this.currentEvent.relatedPersonName ?? ''),
        location: this.currentEvent.location ?? '',
        notes: this.currentEvent.notes ?? '',
      }, { emitEvent: false });

      this.relatedPersonSearchControl.setValue(
        this.currentEvent.relatedPersonId ? (this.currentEvent.relatedPersonName ?? '') : '',
        { emitEvent: false },
      );
    }

    this.syncPlaceRules();
    this.syncAnniversaryRules();
    this.syncRelatedPersonRules();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get title(): string {
    return this.currentEvent ? 'Editar evento espiritual' : 'Nuevo evento espiritual';
  }

  get isWeddingAnniversary(): boolean {
    return this.form.get('religiousEventType')?.value === 'WeddingAnniversary';
  }

  get supportsRelatedPerson(): boolean {
    const value = this.form.get('religiousEventType')?.value;
    return value === 'Wedding' || value === 'WeddingAnniversary' || value === 'Engagement';
  }

  get relatedPersonMode(): RelatedPersonMode {
    return this.form.get('relatedPersonMode')?.value ?? 'none';
  }

  get placeMode(): ReligiousEventPlaceMode {
    return this.form.get('placeMode')?.value ?? 'unit';
  }

  get visibleReligiousEvents(): PersonEvent[] {
    return this.religiousEvents.slice().sort((a, b) => b.eventDate.localeCompare(a.eventDate));
  }

  formatDate(value: string): string {
    if (!value) return '-';
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  }

  displayReligiousEventOption(option: ReligiousEventTypeOption | string | null): string {
    if (!option) return '';
    return typeof option === 'string' ? option : option.label;
  }

  onReligiousEventTypeSelected(option: ReligiousEventTypeOption): void {
    this.form.patchValue({ religiousEventType: option.value });
    this.religiousEventSearchControl.setValue(option.label, { emitEvent: false });
    this.religiousEventSearchControl.updateValueAndValidity({ emitEvent: false });
  }

  clearReligiousEventType(): void {
    this.form.patchValue({ religiousEventType: null });
    this.religiousEventSearchControl.setValue('');
  }

  private _religiousEventTypeValidator() {
    return (): { required: true } | null => {
      return this.form?.get('religiousEventType')?.value ? null : { required: true };
    };
  }

  displayPersonOption(option: PersonLookupOption | string | null): string {
    if (!option) {
      return '';
    }

    return typeof option === 'string' ? option : option.name;
  }

  onRelatedPersonSelected(option: PersonLookupOption): void {
    this.form.patchValue({
      relatedPersonId: option.id,
      relatedPersonName: '',
    }, { emitEvent: false });
    this.relatedPersonSearchControl.setValue(option.name, { emitEvent: false });
  }

  clearSelectedRelatedPerson(): void {
    this.form.patchValue({ relatedPersonId: null }, { emitEvent: false });
    this.relatedPersonSearchControl.setValue('', { emitEvent: false });
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onSubmit(): void {
    this.religiousEventSearchControl.markAsTouched();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notifications.error('Completa los datos requeridos antes de guardar.');
      return;
    }

    const category = getPersonEventCategoryForKind('religious-event');
    if (!category) {
      this.notifications.error('No se pudo determinar el tipo de evento.');
      return;
    }

    const raw = this.form.getRawValue();
    const location = (raw.location || '').trim();
    const request: CreatePersonEventRequest = {
      category,
      eventDate: this.toIsoDate(raw.eventDate),
      locationOrganizationUnitId: raw.placeMode === 'unit' ? (raw.locationOrganizationUnitId || undefined) : undefined,
      religiousEventType: raw.religiousEventType ?? undefined,
      anniversaryYears: raw.anniversaryYears ? Number(raw.anniversaryYears) : undefined,
      relatedPersonId: raw.relatedPersonMode === 'system' ? (raw.relatedPersonId || undefined) : undefined,
      relatedPersonName: raw.relatedPersonMode === 'manual'
        ? ((raw.relatedPersonName || '').trim() || undefined)
        : undefined,
      location: raw.placeMode === 'location' ? (location || undefined) : undefined,
      notes: raw.notes || undefined,
    };

    this.isSaving = true;
    const operation$: Observable<unknown> = this.currentEvent
      ? this.personEventService.updateEvent(this.person.id, category, this.currentEvent.id, request)
      : this.personEventService.createEvent(this.person.id, request);

    operation$.subscribe({
      next: () => {
        this.isSaving = false;
        this.notifications.success(
          this.currentEvent ? 'Registro actualizado correctamente.' : 'Registro agregado correctamente.',
        );
        this.saved.emit();
      },
      error: (err: unknown) => {
        this.isSaving = false;
        this.notifications.error(getPersonEventEditorErrorMessage(
          err,
          this.currentEvent ? 'No se pudo actualizar el registro.' : 'No se pudo guardar el registro.',
        ));
      },
    });
  }

  private syncAnniversaryRules(): void {
    const anniversaryControl = this.form.get('anniversaryYears');

    if (this.isWeddingAnniversary) {
      anniversaryControl?.setValidators([Validators.required]);
    } else {
      anniversaryControl?.clearValidators();
      anniversaryControl?.setValue(null, { emitEvent: false });
    }

    anniversaryControl?.updateValueAndValidity({ emitEvent: false });
  }

  private syncPlaceRules(): void {
    const locationOrganizationUnitIdControl = this.form.get('locationOrganizationUnitId');
    const locationControl = this.form.get('location');

    if (this.placeMode === 'unit') {
      locationOrganizationUnitIdControl?.setValidators([Validators.required]);
      locationControl?.setValidators([Validators.maxLength(200)]);
      locationControl?.setValue('', { emitEvent: false });
    } else {
      locationControl?.setValidators([Validators.required, Validators.maxLength(200)]);
      locationOrganizationUnitIdControl?.clearValidators();
      locationOrganizationUnitIdControl?.setValue(null, { emitEvent: false });
    }

    locationOrganizationUnitIdControl?.updateValueAndValidity({ emitEvent: false });
    locationControl?.updateValueAndValidity({ emitEvent: false });
  }

  private syncRelatedPersonRules(): void {
    const relatedPersonIdControl = this.form.get('relatedPersonId');
    const relatedPersonNameControl = this.form.get('relatedPersonName');

    if (!this.supportsRelatedPerson) {
      this.form.patchValue({
        relatedPersonMode: 'none',
        relatedPersonId: null,
        relatedPersonName: '',
      }, { emitEvent: false });
      this.relatedPersonSearchControl.setValue('', { emitEvent: false });
      relatedPersonIdControl?.clearValidators();
      relatedPersonNameControl?.clearValidators();
      relatedPersonIdControl?.updateValueAndValidity({ emitEvent: false });
      relatedPersonNameControl?.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (this.relatedPersonMode === 'system') {
      relatedPersonIdControl?.setValidators([Validators.required]);
      relatedPersonNameControl?.clearValidators();
      relatedPersonNameControl?.setValue('', { emitEvent: false });
    } else if (this.relatedPersonMode === 'manual') {
      relatedPersonNameControl?.setValidators([Validators.required, Validators.maxLength(200)]);
      relatedPersonIdControl?.clearValidators();
      relatedPersonIdControl?.setValue(null, { emitEvent: false });
      this.relatedPersonSearchControl.setValue('', { emitEvent: false });
    } else {
      relatedPersonIdControl?.clearValidators();
      relatedPersonNameControl?.clearValidators();
      relatedPersonIdControl?.setValue(null, { emitEvent: false });
      relatedPersonNameControl?.setValue('', { emitEvent: false });
      this.relatedPersonSearchControl.setValue('', { emitEvent: false });
    }

    relatedPersonIdControl?.updateValueAndValidity({ emitEvent: false });
    relatedPersonNameControl?.updateValueAndValidity({ emitEvent: false });
  }

  private toIsoDate(value: any): string {
    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private fromIsoDate(value?: string | null): any | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }

    return this.dateAdapter.createDate(year, month - 1, day);
  }
}
